import {
  Autosuggest,
  Box,
  Button,
  ColumnLayout,
  DatePicker,
  ExpandableSection,
  Input,
  Modal,
  Multiselect,
  Select,
  SelectProps,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PlaceList, PlaceRequest } from '../common/types';
import { addPlacesToList, getAllLists, getLists } from '../common/api/lists';
import { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Header,
  AppLayout,
  ContentLayout,
  Grid,
  Toggle,
  Form,
  Alert,
  FormField,
  StatusIndicator,
} from '@cloudscape-design/components';
import { ErrorMessage } from './ErrorMessage';
import { CreateListModal } from './CreateListModal';

export type AddToListModalProps = {
  places: { uuid: string }[];
  open: boolean;
  setOpen: (open: boolean) => void;
  onComplete?: () => void;
};

export function AddToListModal(props: AddToListModalProps) {
  const { open, setOpen, onComplete, places } = props;

  const [createListOpen, setCreateListOpen] = useState<boolean>(false);
  const [selectedLists, setSelectedLists] = useState<PlaceList[]>([]);

  const [now, setNow] = useState<Date>(new Date());

  const listQuery = useQuery({
    queryKey: ['lists', 'all', now],
    queryFn: getAllLists,
    enabled: open,
  });

  const listLookup = useMemo(() => {
    return listQuery.data?.reduce(
      (acc, list) => {
        acc[list.uuid] = list;
        return acc;
      },
      {} as Record<string, PlaceList>,
    );
  }, [listQuery.data]);

  const options = useMemo(() => {
    return listQuery.data?.map((list) => {
      return {
        label: list.name,
        value: list.uuid,
        description: list.description ?? undefined,
      };
    });
  }, [listQuery.data]);

  const selectedOptions = useMemo(() => {
    return selectedLists.map((list) => {
      return {
        label: list.name,
        value: list.uuid,
        description: list.description ?? undefined,
      };
    });
  }, [selectedLists]);

  const close = () => {
    setOpen(false);
    setSelectedLists([]);
  };

  const addList = useMutation({
    mutationFn: async ({ lists, req }: { lists: PlaceList[]; req: PlaceRequest }) => {
      for (const list of lists) {
        await addPlacesToList(list.uuid, req);
      }
    },
    onSuccess: () => {
      if (onComplete) {
        onComplete();
      }
      addList.reset();
      close();
    },
  });

  const isInvalid = !places || !selectedLists;

  const handleCreate = () => {
    if (addList.isPending) {
      return;
    }
    if (isInvalid) {
      return;
    }

    const data: PlaceRequest = {
      place_uuids: places.map((place) => place.uuid),
    };
    addList.mutate({ lists: selectedLists, req: data });
  };

  return (
    <Modal
      size="large"
      onDismiss={close}
      visible={open}
      closeAriaLabel="Close"
      header="Add to List"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close}>
              Cancel
            </Button>
            <Button
              loading={addList.isPending}
              variant="primary"
              onClick={handleCreate}
              disabled={isInvalid}
            >
              Add
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField
          description="The lists to add the places to."
          label="Lists"
          secondaryControl={
            <Button variant="link" onClick={() => setCreateListOpen(true)} iconName="add-plus">
              Create
            </Button>
          }
        >
          <Multiselect
            selectedOptions={selectedOptions}
            onChange={({ detail }) =>
              setSelectedLists(
                listLookup
                  ? detail.selectedOptions.map((option) => listLookup[option.value as string])
                  : [],
              )
            }
            options={options || []}
            loadingText="Loading lists..."
            placeholder="Select lists..."
            statusType={listQuery.isLoading ? 'loading' : listQuery.isError ? 'error' : 'finished'}
            empty={
              <Box textAlign="center" color="inherit">
                No lists found
              </Box>
            }
          />
          <CreateListModal
            open={createListOpen}
            setOpen={setCreateListOpen}
            onComplete={(placeList) => {
              listQuery.refetch();
              setSelectedLists([...selectedLists, placeList]);
            }}
          />
        </FormField>
        {addList.error && <ErrorMessage loading={addList.isPending} error={addList.error} />}
      </SpaceBetween>
    </Modal>
  );
}
